// SolarNEST Rooftop mounting system
// Initial implementation – component codes and spacing rules to be refined from SolarNEST specs

import { StudioSystemType } from 'types/global'
import { FeatureFlagName } from 'util/featureFlags'
import { BomGeneratorAbstract } from '../bom_generators/BomGeneratorAbstract'
import {
  PerColumnRailBomGeneratorAbstract,
  VerticalRailPosition,
} from '../bom_generators/PerColumnRailBomGeneratorAbstract'
import {
  HorizontalRailPosition,
  PerRowRailBomGeneratorAbstract,
} from '../bom_generators/PerRowRailBomGeneratorAbstract'
import { getPanelLayoutDimensions, hasUniformRowLengths } from '../compatibilityLayout'
import {
  BasicSpacingLayoutAdjusterAbstract,
  SpacingRuleSet,
} from '../layout_adjusters/BasicSpacingLayoutAdjusterAbstract'
import { MountingSystemAbstract } from '../MountingSystemAbstract'
import { CutAndSplicerAbstract } from '../post_processors/CutAndSplicerAbstract'
import { RoofHookAdderAbstract } from '../post_processors/RoofHookAdderAbstract'
import { getRailDirection } from '../railing'
import {
  CompatibilityParameters,
  Component,
  Direction,
  Fastener,
  Item,
  MountingCalcInput,
  MountingCalcResult,
  MountingComponentType,
  Panel,
  PanelBlock,
  RailComponent,
  RailType,
} from '../types'
import { addItems, createMountingItem } from '../utils'
import { SOLARNEST_REQUIRED_TILT_DEGREES } from './solarNestWindSnowMax'

const spacingRuleSet: SpacingRuleSet = {
  interPanel: { horizontalSpacing: 20, verticalSpacing: 20 },
}

class SolarNestRooftopSpacingAdjuster extends BasicSpacingLayoutAdjusterAbstract {
  spacingRuleSet = spacingRuleSet
}

const railOverhang = 100

class SolarNestRooftopBomGenerator extends BomGeneratorAbstract {
  async generateBom(result: MountingCalcResult): Promise<MountingCalcResult> {
    const tiltOption = this.input.options.tiltOption as string | undefined
    if (tiltOption !== 'eastWestTilt' && tiltOption !== 'tripleTilt') {
      return {
        ...result,
        items: [],
        fasteners: {},
      }
    }

    const totalPanels = this.countCurrentBlockPanels()
    const modulesPerRow = this.countModulesPerRow()

    const items: Item[] = []
    const isTripleTilt = tiltOption === 'tripleTilt'

    // Calculate bracket quantities once and reuse them
    let bracketC1Qty: number
    let bracketC2Qty: number = 0
    let bracketC3Qty: number = 0

    if (isTripleTilt) {
      bracketC1Qty = Math.floor(modulesPerRow / 2)
      bracketC2Qty = Math.floor(modulesPerRow / 2) + 1
      bracketC3Qty = Math.floor(modulesPerRow / 2)
    } else {
      bracketC1Qty = modulesPerRow
    }

    // Use stored quantities for creating brackets
    items.push(...this.createBracketC1(bracketC1Qty))
    if (isTripleTilt) {
      items.push(...this.createBracketC2(bracketC2Qty))
      items.push(...this.createBracketC3(bracketC3Qty))
    }

    // Common components (same for both tilt options)
    const pedestalQty = totalPanels * 2 + 2
    items.push(...this.createPedestal(pedestalQty))

    items.push(...this.createTiltBracket(pedestalQty))

    items.push(...this.createTopClampAssembly(totalPanels * 4))

    let m12x50HexBoltQty: number
    if (isTripleTilt) {
      m12x50HexBoltQty = Math.floor(totalPanels / 2) + 1
    } else {
      m12x50HexBoltQty = (Math.floor(totalPanels / 2) + 1) * 2
    }
    items.push(...this.createM12x50HexBolt(m12x50HexBoltQty))

    let m12x50SerratedHexBoltQty: number
    if (isTripleTilt) {
      m12x50SerratedHexBoltQty = bracketC1Qty + bracketC2Qty + bracketC3Qty
    } else {
      m12x50SerratedHexBoltQty = bracketC1Qty
    }
    items.push(...this.createM12x50SerratedHexBolt(m12x50SerratedHexBoltQty))

    // M12x25mm HEX BOLT: Reuse stored bracket quantities
    let m12x25HexBoltQty: number
    if (isTripleTilt) {
      m12x25HexBoltQty = bracketC1Qty + bracketC2Qty + bracketC3Qty
    } else {
      m12x25HexBoltQty = bracketC1Qty
    }
    items.push(...this.createM12x25HexBolt(m12x25HexBoltQty))

    // M12 SERRATED FLANGE NUT: Sum of all bolts
    const m12SerratedNutQty = m12x50HexBoltQty + m12x50SerratedHexBoltQty + m12x25HexBoltQty
    items.push(...this.createM12SerratedNut(m12SerratedNutQty))

    return addItems(result, items)
  }

  private countCurrentBlockPanels(): number {
    let count = 0
    this.block.rows.forEach((row) => {
      row.forEach((panel) => {
        if (panel.isActive) {
          count++
        }
      })
    })
    return count
  }

  private countModulesPerRow(): number {
    if (!this.block.rows.length) {
      return 0
    }
    // System always represents layout as landscape-oriented, so rows represent horizontal span
    return this.block.rows.length
  }

  private createBracketC1(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'SNRT-411-114-110',
      components: [
        {
          name: 'SNRT-411-114-110',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createBracketC2(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'SNRT-412-114-110',
      components: [
        {
          name: 'SNRT-412-114-110',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createBracketC3(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'SNRT-413-114-110',
      components: [
        {
          name: 'SNRT-413-114-110',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createPedestal(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'SNRT-321-001-501',
      components: [
        {
          name: 'SNRT-321-001-501',
          type: MountingComponentType.ROOF_HOOK,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createTiltBracket(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'SN-121-00-966',
      components: [
        {
          name: 'SN-121-00-966',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createTopClampAssembly(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'SN-111-00-500',
      components: [
        {
          name: 'SN-111-00-500',
          type: MountingComponentType.CLAMP,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createM12x50HexBolt(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'HDW-HB-M12-50-761',
      components: [
        {
          name: 'HDW-HB-M12-50-761',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createM12x50SerratedHexBolt(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'HDW-SFB-M12-50-761',
      components: [
        {
          name: 'HDW-SFB-M12-50-761',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createM12x25HexBolt(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'HDW-HB-M12-25-761',
      components: [
        {
          name: 'HDW-HB-M12-25-761',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createM12SerratedNut(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'HDW-SFN-M12-76',
      components: [
        {
          name: 'HDW-SFN-M12-76',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }
}

export class SolarNestRooftop extends MountingSystemAbstract {
  layoutAdjusterClass = SolarNestRooftopSpacingAdjuster
  bomGeneratorClass = SolarNestRooftopBomGenerator

  getFastener(): Fastener {
    const railDirection = getRailDirection(this.input.panelBlocks[0].moduleGrid.moduleLayout(), this.input.options)
    return chooseFastener(chooseHook(this.input, null, chooseRail(this.input, railDirection)))
  }

  getCompatibilityParameters(): CompatibilityParameters {
    return {
      integrations: [],
      moduleThicknessRange: [30, 46],
      roofHeightRange: [0, 30],
      roofTypeRequired: true,
      featureFlag: 'mounting_solarnest_rooftop' as FeatureFlagName,
      panelLayoutRange: { columns: [1, 1], rows: [4, 16] },
    }
  }
}

export function validateSolarNestRooftopCompatibility(system: StudioSystemType): boolean {
  const mountingName = system?.mounting
  if (mountingName !== 'solarNestRooftop') return true

  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`${mountingName.toUpperCase()}_PANEL_SETUP_INFO`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`${mountingName.toUpperCase()}_FACING_INFO`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`${mountingName.toUpperCase()}_INPUT_WARNING`, system.uuid, 'plugin')

  if (system.moduleGrids().length === 0) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate(
        'Panels must be placed first. Please deselect SolarNEST and add panels using Dual Tilt racking type and Landscape panel orientation in panel settings'
      ),
      key: `${mountingName.toUpperCase()}_PANEL_SETUP_INFO`,
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
    return true
  }

  const allGridsValid = system.moduleGrids().every((moduleGrid) => {
    const { numRows } = getPanelLayoutDimensions(moduleGrid.cellsActive)
    const isUniform = hasUniformRowLengths(moduleGrid.cellsActive)
    const hasEvenRows = numRows % 2 === 0
    return isUniform && hasEvenRows
  })
  if (!allGridsValid) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate('Panel layout must be between 1x4 and 1x16 with an even number of rows'),
      key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
    return false
  }

  const snIntegrationData = system.integration_json?.solarNestRooftop
  const snTiltOption = snIntegrationData?.tiltOption as string | undefined
  if (snTiltOption === 'eastWestTilt') {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate('Selected mounting system is designed to have dual tilt facing East/West'),
      key: `${mountingName.toUpperCase()}_FACING_INFO`,
      severity: 'info',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
  }

  if (snTiltOption === 'eastWestTilt' || snTiltOption === 'tripleTilt') {
    const tiltMismatch = system.moduleGrids().some((moduleGrid) => {
      const slope = Math.round(moduleGrid.getSlope() * 10) / 10
      const tilt = Math.round((moduleGrid.panelTiltOverride ?? 0) * 10) / 10
      const relativeTilt = Math.round((tilt - slope) * 10) / 10
      if (relativeTilt !== SOLARNEST_REQUIRED_TILT_DEGREES) return true
      if (snTiltOption === 'tripleTilt') {
        const roll =
          Math.round((typeof (moduleGrid as any).getRoll === 'function' ? (moduleGrid as any).getRoll() : 0) * 10) / 10
        if (roll !== SOLARNEST_REQUIRED_TILT_DEGREES) return true
      }
      return false
    })
    if (tiltMismatch) {
      const message =
        snTiltOption === 'tripleTilt'
          ? window.translate(
              'Selected mounting system requires relative tilt to be %{relativeTilt} degrees and triple tilt to be between 0-%{tripleTilt} degrees',
              { relativeTilt: SOLARNEST_REQUIRED_TILT_DEGREES, tripleTilt: SOLARNEST_REQUIRED_TILT_DEGREES }
            )
          : window.translate('Selected mounting system requires relative tilt to be %{degrees} degrees', {
              degrees: SOLARNEST_REQUIRED_TILT_DEGREES,
            })
      window.WorkspaceHelper.addProjectErrorToReduxStore({
        message,
        key: `${mountingName.toUpperCase()}_INPUT_WARNING`,
        severity: 'warning',
        systemId: system.uuid,
        source: 'plugin',
        category: 'mounting',
        options: {},
      })
      return false
    }
  }

  return true
}

class SolarNestRooftopHorizontalRailBomGenerator extends PerRowRailBomGeneratorAbstract {
  chooseRail() {
    return chooseRail(this.input, 'horizontal')
  }

  chooseMidClamp() {
    return chooseMidClamp(this.input)
  }

  chooseEndClamp() {
    return chooseEndClamp(this.input)
  }

  chooseEndCap() {
    return chooseEndCap(this.input)
  }

  getRailPositions(panel: Panel): HorizontalRailPosition[] {
    const distanceFromRailToPanelEdge = (panel.height * 0.6) / 2
    return [
      {
        top: distanceFromRailToPanelEdge,
        left: -railOverhang,
        right: railOverhang,
      },
      {
        top: panel.height - distanceFromRailToPanelEdge,
        left: -railOverhang,
        right: railOverhang,
      },
    ]
  }
}

class SolarNestRooftopVerticalRailBomGenerator extends PerColumnRailBomGeneratorAbstract {
  chooseRail() {
    return chooseRail(this.input, 'vertical')
  }

  chooseMidClamp() {
    return chooseMidClamp(this.input)
  }

  chooseEndClamp() {
    return chooseEndClamp(this.input)
  }

  chooseEndCap() {
    return chooseEndCap(this.input)
  }

  getRailPositions(panel: Panel): VerticalRailPosition[] {
    const distanceFromRailToPanelEdge = (panel.width * 0.6) / 2
    return [
      {
        left: distanceFromRailToPanelEdge,
        top: -railOverhang,
        bottom: railOverhang,
      },
      {
        left: panel.width - distanceFromRailToPanelEdge,
        top: -railOverhang,
        bottom: railOverhang,
      },
    ]
  }
}

function chooseRail(input: MountingCalcInput, direction: Direction): RailComponent {
  const railLength = (input.options.railLength as number) ?? 4200
  return {
    type: MountingComponentType.RAIL,
    railType: 'continuous',
    direction,
    name: `SN-R-${railLength}`,
    length: 0,
    top: 0,
    left: 0,
  }
}

function chooseMidClamp(_input: MountingCalcInput): Item {
  return {
    name: 'SN-MID-CLAMP',
    components: [
      {
        name: 'SN-MID-CLAMP',
        type: MountingComponentType.CLAMP,
        left: -10,
        top: -10,
      },
    ],
  }
}

function chooseEndClamp(_input: MountingCalcInput): Item {
  return {
    name: 'SN-END-CLAMP',
    components: [
      {
        name: 'SN-END-CLAMP',
        type: MountingComponentType.CLAMP,
        left: -10,
        top: -10,
      },
    ],
  }
}

function chooseEndCap(_input: MountingCalcInput): Item {
  return {
    name: 'SN-END-CAP',
    components: [
      {
        name: 'SN-END-CAP',
        type: MountingComponentType.OTHER,
        left: -18,
        top: -24,
      },
    ],
  }
}

function chooseHook(input: MountingCalcInput, _panelBlock: PanelBlock | null, _rail: RailComponent): Item[] {
  const componentCode = (input.options.roofHook as string) ?? 'SN-ROOF-HOOK'
  return [
    {
      name: componentCode,
      components: [
        {
          name: componentCode,
          type: MountingComponentType.ROOF_HOOK,
          left: -10,
          top: -10,
        },
      ],
    },
  ]
}

function chooseFastener(_hooks: Item[]): Fastener {
  return {
    includedInRoofHookProduct: false,
    name: 'SN-FASTENER',
    qtyPerRoofHook: 2,
    length: 70,
    diameter: 6.3,
    components: [],
  }
}

class SolarNestRooftopRoofHookAdder extends RoofHookAdderAbstract {
  spacingRuleSet = spacingRuleSet
  railOverhang = railOverhang

  getRailType(_input: MountingCalcInput): RailType {
    return 'continuous'
  }

  chooseHook(input: MountingCalcInput, panelBlock: PanelBlock, rail: RailComponent) {
    return chooseHook(input, panelBlock, rail)
  }

  chooseFastener(hook: Item[]) {
    return chooseFastener(hook)
  }
}

class SolarNestRooftopRailSplicer extends CutAndSplicerAbstract {
  isTarget(component: Component): boolean {
    return component?.type === MountingComponentType.RAIL
  }

  chooseSplice(_railName: string, _input: MountingCalcInput): Item[] {
    return [
      {
        name: 'SN-SPLICE',
        components: [
          {
            type: MountingComponentType.SPLICE,
            name: 'SN-SPLICE',
            top: 0,
            left: 0,
          },
        ],
      },
    ]
  }

  getFullLength(railName: string): number {
    const match = railName.match(/SN-R-(\d+)/)
    return match ? parseInt(match[1], 10) : 4200
  }
}
